/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Send, Clock, CheckCircle2, XCircle, Eye, Plus, ArrowLeft, ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MultiImageUploader } from '../../components/MultiImageUploader';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'Pending Review' },
  approved: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Approved & Published' },
  declined: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Declined' },
};

const PortalBlogSubmission = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', excerpt: '', image_url: '', gallery_images: [], category: 'member_blog' });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/portal/blog/submissions`, { withCredentials: true });
      setSubmissions(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/portal/profile/gallery`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim() || !formData.excerpt.trim()) {
      toast.error('Please fill in title, content, and excerpt');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/blog/submit`, formData, { withCredentials: true });
      toast.success('Blog submitted for review!');
      setFormData({ title: '', content: '', excerpt: '', image_url: '', gallery_images: [], category: 'member_blog' });
      setView('list');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit blog');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-blog-submission">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-indigo-500 rounded-sm flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Blog Submission</h1>
        </div>
        <p className="text-slate-600 mt-2">
          Share your expertise with the chamber community. Submitted blogs are reviewed before publishing.
        </p>
      </motion.div>

      {/* Workflow Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6"
        data-testid="workflow-info"
      >
        <h3 className="font-semibold text-blue-800 text-sm mb-2">How It Works</h3>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs text-blue-700">
          <span className="flex items-center"><span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-1.5 text-xs font-bold">1</span>Submit your blog</span>
          <span className="hidden sm:inline text-blue-300">&rarr;</span>
          <span className="flex items-center"><span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-1.5 text-xs font-bold">2</span>Admin reviews content</span>
          <span className="hidden sm:inline text-blue-300">&rarr;</span>
          <span className="flex items-center"><span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-1.5 text-xs font-bold">3</span>Approved = Published publicly</span>
        </div>
      </motion.div>

      {/* View Toggle */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setView('list')}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'list' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="view-submissions-btn"
        >
          My Submissions ({submissions.length})
        </button>
        <button
          onClick={() => setView('new')}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center ${view === 'new' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="new-blog-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Blog Post
        </button>
      </div>

      {/* Submission Form */}
      {view === 'new' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#002249]">Write Your Blog Post</h2>
              <p className="text-sm text-slate-500 mt-1">Your submission will be reviewed by a content admin before publishing.</p>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="blog-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Blog Title <span className="text-[#E63946]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter an engaging title for your blog post"
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="blog-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Excerpt / Summary <span className="text-[#E63946]">*</span>
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={e => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Write a short summary (2-3 sentences) that will appear in the blog listing..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="blog-excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Full Content <span className="text-[#E63946]">*</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your full blog post here. Be informative, professional, and engaging..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[250px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="blog-content-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Featured Image (optional)</label>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center px-4 py-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 text-sm" data-testid="blog-image-upload">
                    <ImageIcon className="w-4 h-4 mr-2 text-slate-500" />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                  {formData.image_url && (
                    <Img variant="generic" src={resolveImg(formData.image_url)} alt="Preview" className="w-16 h-16 object-cover rounded-sm border" />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Image Gallery (optional) <span className="text-xs text-slate-500 font-normal">— up to 10 images, shown as a carousel inside the blog</span>
                </label>
                <MultiImageUploader
                  value={formData.gallery_images || []}
                  onChange={(urls) => setFormData(prev => ({ ...prev, gallery_images: urls }))}
                  max={10}
                  label="Add gallery photos"
                  dataTestId="blog-gallery-uploader"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="blog-category-select"
                >
                  <option value="member_blog">Member Blog</option>
                  <option value="business_insights">Business Insights</option>
                  <option value="community_stories">Community Stories</option>
                  <option value="industry_news">Industry News</option>
                </select>
              </div>

              <div className="pt-4 flex space-x-3">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setView('list')} data-testid="cancel-blog-btn">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="submit-blog-btn">
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Submissions List */}
      {view === 'list' && (
        <div className="space-y-3" data-testid="submissions-list">
          {submissions.length > 0 ? submissions.map((sub, index) => {
            const status = statusConfig[sub.status] || statusConfig.pending;

            return (
              <motion.div
                key={sub.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className="bg-white border border-slate-200 rounded-sm p-5"
                data-testid={`submission-item-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="font-semibold text-[#002249] mb-1">{sub.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{sub.excerpt}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{new Date(sub.created_at).toLocaleDateString()}</span>
                      <span className="text-slate-300">|</span>
                      <span className="capitalize">{sub.category?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm flex-shrink-0 flex items-center ${status.color}`}>
                    <status.icon className="w-3.5 h-3.5 mr-1" />
                    {status.label}
                  </span>
                </div>

                {sub.feedback && (
                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-sm text-sm">
                    <p className="font-medium text-slate-700 mb-1">Reviewer Feedback:</p>
                    <p className="text-slate-600">{sub.feedback}</p>
                    {sub.reviewer_name && (
                      <p className="text-xs text-slate-400 mt-1">— {sub.reviewer_name}</p>
                    )}
                  </div>
                )}

                {sub.status === 'approved' && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-sm text-sm text-green-700 flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    This blog has been approved and published to the public blog page.
                  </div>
                )}

                {sub.status === 'declined' && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700 flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    This blog was declined and will not be published.
                  </div>
                )}
              </motion.div>
            );
          }) : (
            <div className="text-center py-16 text-slate-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No blog submissions yet</p>
              <p className="text-sm mt-1">Share your expertise with the community.</p>
              <Button className="mt-4 bg-[#002249] hover:bg-[#001630] rounded-sm" onClick={() => setView('new')} data-testid="write-first-blog-btn">
                <Plus className="w-4 h-4 mr-2" /> Write Your First Blog
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Detail View */}
      {view === 'detail' && selectedSubmission && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => { setView('list'); setSelectedSubmission(null); }} className="flex items-center text-sm text-slate-600 hover:text-[#002249] mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to submissions
          </button>
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-bold text-[#002249] mb-2">{selectedSubmission.title}</h2>
            <p className="text-slate-600 whitespace-pre-wrap">{selectedSubmission.content}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PortalBlogSubmission;
